/**
 * Turning a tab strip's routes into the `href`s its anchors carry (BOS-130).
 *
 * Every tab already owns a distinct route in its `*TabsMeta`, so
 * there is nothing here to invent — the URL-per-tab mapping existed long before
 * the tabs became real links. What this adds is the one thing a bare path
 * cannot express: **the query string the shell is holding**.
 *
 * Books and Statements keep a date range in the URL (`?from=…&to=…`) and share
 * it across their tabs — moving from the trial balance to the journal must not
 * silently reset the window being read. Their old `onTabChange` appended
 * `location.search` before navigating; an anchor whose `href` is the bare path
 * would drop it, which is a quiet reporting bug rather than a cosmetic one.
 *
 * Operations, Registers, Configurations and Analytics hold no shell-level range,
 * so they pass no params and their `href` is the path unchanged. That is deliberate
 * rather than an oversight: the Activity tab's `?key=` deep link belongs to
 * *that* tab, and carrying it onto Templates would filter a screen by an
 * operation the user just navigated away from.
 *
 * Lives at the `pages` root, one level above `accounting`: `/analytics` is a tab
 * shell too, so this is no longer an accounting helper — and a reporting area
 * reaching into the ledger's folder for it would make a dependency real that
 * isn't. Same reason it sat at the `accounting` root before: more than one shell
 * needs it, and it belongs to none of them.
 */

/** A tab plus the `href` its anchor renders. */
export type LinkedTab<T extends { path: string }> = T & { href: string };

/**
 * Attach an `href` to every tab, carrying `params` when there are any.
 *
 * Pass the shell's current `searchParams` to preserve them across a tab change;
 * omit it for a shell that holds none.
 */
export const linkTabs = <T extends { path: string }>(
    tabs: readonly T[],
    params?: URLSearchParams,
): LinkedTab<T>[] => {
    const search = params?.toString() ?? '';

    return tabs.map((tab) => ({
        ...tab,
        href: search ? `${tab.path}?${search}` : tab.path,
    }));
};
